import React from 'react';
import { useIntl } from 'react-intl';

import { Item } from '../../types/Item';
import { Box } from '../Box/Box';
import { Chip } from '../Chip/Chip';
import { CountChip } from '../Chip/subcomponents/CountChip';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Layout } from '../Layout/Layout';
import { MarkedText } from '../MarkedText/MarkedText';
import { Text } from '../Text/Text';
import { PaddingMode } from './types';

interface Props<D> {
  bg?: string;
  borderRadius?: 'm';
  chipPosition?: 'left' | 'right';
  isVisible: boolean;
  onClick: (event: MouseEvent) => void;
  item: Item<D>;
  minH?: string;
  togglePosition?: 'left' | 'right';
  padding?: PaddingMode;
  search?: string;
}

export const AccordionItemHeader = <D,>({
  bg,
  borderRadius,
  isVisible,
  item,
  minH = 'header.height',
  togglePosition = 'left',
  padding,
  onClick,
  search,
  chipPosition = 'left',
}: Props<D>) => {
  const intl = useIntl();

  const {
    chip,
    count,
    edit,
    hash,
    headerBorder = 'none',
    headerClass,
    headerContentAlignment = 'center',
    isLoading,
    summary,
    title,
  } = item;

  const showEditAction = edit != null;
  const isEditing = edit?.isEditing;

  return (
    <Layout
      as="summary"
      align="center"
      aria-controls={`${title}-panel`}
      aria-expanded={isVisible}
      className={headerClass}
      disabled={isLoading}
      minH={minH}
      id={hash || `${title}-header`}
      justify="space-between"
      px={padding === 'all' ? 4 : 0}
      py={headerContentAlignment === 'start' ? 2 : 0}
      role="button"
      spacing={2}
      styles={[
        componentStyles.summary({
          enableBottomBorder: isVisible && headerBorder === 'bottom',
        }),
        ...(showEditAction && !isEditing
          ? [componentStyles.summaryWithEdit]
          : []),
      ]}
      onClick={onClick}
      bg={bg}
      borderRadius={borderRadius}
    >
      <Layout align={headerContentAlignment} flex="auto" spacing={2}>
        {togglePosition === 'left' && (
          <Icon icon={isVisible ? 'chevron-down' : 'chevron-right'} />
        )}
        {summary || (
          <Text variant="body-bold">
            <MarkedText search={search} text={title} />
          </Text>
        )}
        {showEditAction && (
          <Box
            data-accordion-edit-action
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            styles={!isEditing ? componentStyles.editActionHidden : undefined}
          >
            <IconButton
              icon="edit"
              onClick={() => edit?.onClick()}
              size="xs"
              disabled={isEditing}
              tooltip={
                isEditing
                  ? intl.formatMessage({
                      id: 'AccordionItemHeader.saveToExitEditTooltip',
                      defaultMessage: 'Save to exit edit',
                    })
                  : intl.formatMessage({
                      id: 'AccordionItemHeader.editTooltip',
                      defaultMessage: 'Edit',
                    })
              }
            />
          </Box>
        )}
        {chip && chipPosition === 'left' && <Chip {...chip} />}
      </Layout>
      <Layout align={headerContentAlignment} flex="none" spacing={2}>
        {chip && chipPosition === 'right' && <Chip {...chip} />}
        {isLoading ? (
          <Icon icon="loading" />
        ) : (
          count && <CountChip count={count} />
        )}
      </Layout>
      {togglePosition === 'right' && (
        <Icon icon={isVisible ? 'chevron-up' : 'chevron-down'} />
      )}
    </Layout>
  );
};

const componentStyles = {
  summary: ({ enableBottomBorder }: { enableBottomBorder: boolean }) => ({
    '::-webkit-details-marker': {
      display: 'none',
    },
    borderBottom: enableBottomBorder ? 'border.divider' : 'none',
  }),
  summaryWithEdit: {
    '&:hover [data-accordion-edit-action]': {
      visibility: 'visible',
    },
  },
  editActionHidden: {
    visibility: 'hidden',
  },
};
